import { useEffect, useState } from "react";
import { useMySubscription } from "@/api/hooks";
import { apiFetch } from "@/api/client";
import { isActiveSubscription } from "@/api/types";
import type { SubscriptionMeResponse, SubscriptionOut } from "@/api/types";
import { tg } from "@/lib/telegram";
import { setActivationModalOpen } from "@/lib/activationGate";
import { SubscriptionActivatedModal } from "@/components/SubscriptionActivatedModal";

/**
 * Глобальний детект активації/продовження підписки + celebration-попап.
 * Монтується в корені (App), тому спрацьовує на БУДЬ-ЯКОМУ екрані (юзер
 * 2026-06-15: активував через адмінку, пішов у головне меню — попапа не було,
 * бо раніше детект жив лише на сторінці /subscription).
 *
 * Логіка: стежимо за ends_at (росте при кожній активації/продовженні).
 * Перший раз бачимо підписку без свіжої оплати → лише baseline (без попапа).
 * Якщо є pending_invoice (юзер щойно платив через WFP) — активний polling.
 */
export function SubscriptionActivationWatcher() {
  const subQuery = useMySubscription();
  const [activatedSub, setActivatedSub] = useState<SubscriptionOut | null>(null);

  // Рефетч підписки при поверненні в аппку (visibility) — щоб піймати
  // активацію, зроблену поза аппкою (напр. через адмінку) (юзер 2026-06-15).
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") void subQuery.refetch();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [subQuery]);

  useEffect(() => {
    // v2 — щоб skinути старі залиплі baseline'и (юзер 2026-06-15: попап
    // не показувався, бо firstEver-baseline «з'їдав» першу активацію).
    const SEEN_KEY = "seen_sub_ends_v2";

    // Крос-девайс дедуп (owner 2026-07-07): факт показу попапа зберігаємо на
    // СЕРВЕРІ (users.metadata.celebrated_sub_ends). Якщо попап уже показали на
    // телефоні — на ПК він НЕ спливе. Порівнюємо по timestamp (не по рядку) щоб
    // не залежати від формату серіалізації дати.
    const markCelebrated = (): void => {
      apiFetch("/subscription/celebrated", { method: "POST" }).catch(() => {});
    };

    const checkActivation = (sub: SubscriptionMeResponse | undefined): boolean => {
      if (!sub || !isActiveSubscription(sub)) return false;
      const endsMs = new Date(sub.ends_at).getTime();
      const celebratedRaw = (sub as { celebrated_ends_at?: string | null })
        .celebrated_ends_at;
      const celebratedMs = celebratedRaw ? new Date(celebratedRaw).getTime() : null;

      // Цю ends_at уже святкували (на будь-якому пристрої) → синхрон, без попапа.
      if (celebratedMs !== null && celebratedMs === endsMs) {
        localStorage.setItem(SEEN_KEY, sub.ends_at);
        return false;
      }
      // Локальний швидкий гард — не блимати повторно до відповіді сервера.
      if (localStorage.getItem(SEEN_KEY) === sub.ends_at) return false;

      // Baseline (cutover / мігровані): сервер ще не фіксував celebrated І нема
      // свіжої оплати → тихо фіксуємо (сервер + локально), БЕЗ попапа. Інакше
      // мігровані при першому вході бачили б хибне святкування.
      if (celebratedMs === null && localStorage.getItem("pending_invoice") === null) {
        localStorage.setItem(SEEN_KEY, sub.ends_at);
        markCelebrated();
        return false;
      }

      // Реальна активація/продовження (celebrated був інший, або є pending_invoice)
      // → показуємо ОДИН раз і фіксуємо на сервері (щоб інші пристрої не показали).
      localStorage.setItem(SEEN_KEY, sub.ends_at);
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      setActivatedSub(sub as unknown as SubscriptionOut);
      markCelebrated();
      // НЕ навігуємо на головну (owner 2026-07-07): попап має з'явитися ПОВЕРХ
      // сторінки, яку юзер відкриває (напр. вакансія по deep-link).
      return true;
    };

    if (checkActivation(subQuery.data)) return;

    const raw = localStorage.getItem("pending_invoice");
    if (!raw) return;
    let pending: { order_reference: string; started_at_before: number } | null = null;
    try {
      pending = JSON.parse(raw);
    } catch {
      localStorage.removeItem("pending_invoice");
      return;
    }
    if (!pending || Date.now() - pending.started_at_before > 15 * 60 * 1000) {
      localStorage.removeItem("pending_invoice");
      return;
    }
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // 90 сек
    const interval = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        clearInterval(interval);
        localStorage.removeItem("pending_invoice");
        return;
      }
      try {
        const r = await subQuery.refetch();
        if (checkActivation(r.data)) {
          localStorage.removeItem("pending_invoice");
          clearInterval(interval);
        }
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subQuery.data && isActiveSubscription(subQuery.data)
      ? subQuery.data.ends_at
      : null,
  ]);

  // Сигналимо InstructionOverlay: поки цей попап відкритий — інструкція чекає.
  useEffect(() => {
    setActivationModalOpen(activatedSub !== null);
    return () => setActivationModalOpen(false);
  }, [activatedSub]);

  if (!activatedSub) return null;
  return (
    <SubscriptionActivatedModal
      sub={activatedSub}
      onClose={() => setActivatedSub(null)}
    />
  );
}
